import { useCallback } from 'react';
import { useSettings } from 'hooks/useSettings';
import { DATE_TIME_INTL_FORMAT } from 'utils/date';
import { THourRanges } from 'components/HoursFilterGroup';
import { UTC_TIME_ZONE } from './config';
import customDayjsPlugin from './customDayjsPlugin';
import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(isBetween);
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(customDayjsPlugin);

export interface DisabledTimes {
	disabledHours?: () => number[];
	disabledMinutes?: (hour: number) => number[];
	disabledSeconds?: (hour: number, minute: number) => number[];
	disabledMilliseconds?: (hour: number, minute: number, second: number) => number[];
}

export interface IUseLocaleTimeUtilsProps {
	selectedTimezone: string;
	parseDate: typeof dayjs;
	getCurrentTimeZoneDate: (date: string | Date | dayjs.Dayjs) => dayjs.Dayjs;
	formatDate: (date: string | Date | dayjs.Dayjs, showTimezone?: boolean, format?: string) => string;
	changeTimezoneToSelectedTimeZone: (date: string | Date | dayjs.Dayjs) => dayjs.Dayjs;
	getTodayParamToFilter: () => string;
	getStartDateParamToFilter: (hoursFilter: number, milliSecondsFilter?: number) => string;
	getHourFilter: (hoursFilter?: THourRanges) => string | null;
	datePickerDisableDatesBeforeToday: (current: dayjs.Dayjs) => boolean;
	disablePastTime: (date: Dayjs) => DisabledTimes;
	getNowInTimeZone: () => dayjs.Dayjs;
}

export const useLocaleTimeUtils = (): IUseLocaleTimeUtilsProps => {
	const { selectedTimezone } = useSettings();

	const changeTimezoneToSelectedTimeZone = useCallback(
		(date: string | Date | dayjs.Dayjs) => {
			const d = dayjs(date);

			// Format just the clock time portion (ignore the original zone)
			const localTimeString = d.format('YYYY-MM-DDTHH:mm:ss');

			// Apply that time to the target timezone — interpreted as local there
			const result = dayjs.tz(localTimeString, selectedTimezone);

			return result;
		},
		[selectedTimezone]
	);

	const getCurrentTimeZoneDate = useCallback(
		(date: string | Date | dayjs.Dayjs) => {
			let dateProcess = dayjs(date).utc();

			if (selectedTimezone !== UTC_TIME_ZONE) {
				dateProcess = dateProcess.tz(selectedTimezone);
			}

			return dateProcess;
		},
		[selectedTimezone]
	);

	const formatDate: IUseLocaleTimeUtilsProps['formatDate'] = useCallback(
		(date, showTimezone, format = DATE_TIME_INTL_FORMAT) => {
			const processDate = getCurrentTimeZoneDate(date);

			const tzDate = processDate.format(format);

			// build string
			if (showTimezone) {
				return `${tzDate} (${selectedTimezone})`;
			}
			return tzDate;
		},
		[getCurrentTimeZoneDate, selectedTimezone]
	);

	const getNowInTimeZone = () => dayjs().tz(selectedTimezone, true);

	const getTodayParamToFilter = () => getNowInTimeZone().startOf('day').transformToBEAcceptanceFormat();

	const getStartDateParamToFilter = (hoursFilter: number, milliSecondsFilter: number = 0): string =>
		dayjs()
			.subtract(hoursFilter, 'hour')
			.subtract(milliSecondsFilter, 'milliseconds')
			.transformToBEAcceptanceFormat();

	const getHourFilter = (hoursFilter?: THourRanges) => {
		if (hoursFilter) {
			switch (hoursFilter) {
				case 'today': {
					return getTodayParamToFilter();
				}
				default: {
					return getStartDateParamToFilter(+hoursFilter);
				}
			}
		}

		return null;
	};

	const datePickerDisableDatesBeforeToday = (current: dayjs.Dayjs) =>
		!!current && current < getNowInTimeZone().startOf('day');

	const disablePastTime = (current: dayjs.Dayjs): DisabledTimes => {
		if (!current) return {};

		const now = getNowInTimeZone();
		if (current.isSame(now, 'day')) {
			const currentHour = now.hour();
			const currentMinute = now.minute();
			const currentSecond = now.second();

			return {
				disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter((i) => i < currentHour),
				disabledMinutes: (selectedHour) =>
					selectedHour === currentHour
						? Array.from({ length: 60 }, (_, i) => i).filter((i) => i < currentMinute)
						: [],
				disabledSeconds: (selectedHour, selectedMinute) =>
					selectedHour === currentHour && selectedMinute === currentMinute
						? Array.from({ length: 60 }, (_, i) => i).filter((i) => i < currentSecond)
						: [],
			};
		}

		return {};
	};

	// ! return
	const api: IUseLocaleTimeUtilsProps = {
		selectedTimezone,
		parseDate: dayjs,
		getCurrentTimeZoneDate,
		formatDate,
		changeTimezoneToSelectedTimeZone,

		// ! Others
		getTodayParamToFilter,
		getStartDateParamToFilter,
		getHourFilter,
		datePickerDisableDatesBeforeToday,
		disablePastTime,
		getNowInTimeZone,
	};

	return api;
};
